/**
 * Create / edit dialog for a notification channel.
 *
 * Type-aware form:
 *   - in_app:    not creatable (singleton)
 *   - email:     recipient list (chips) + subject prefix + SMTP credential
 *   - telegram:  no extra config (chat_id lives on the credential)
 *
 * The credential picker filters to the type required by the channel
 * type (read from /channels/types catalog).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  channelsApi,
  type Channel,
  type ChannelType,
  type ChannelTypeInfo,
} from "@/lib/api-channels";
import type { CredentialType } from "@/lib/api-credentials";
import { cn } from "@/lib/cn";


export type DialogMode =
  | { kind: "create" }
  | { kind: "edit"; channel: Channel };


export interface ChannelDialogProps {
  open: boolean;
  mode: DialogMode;
  onOpenChange: (open: boolean) => void;
}


export function ChannelDialog({ open, mode, onOpenChange }: ChannelDialogProps) {
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const editTarget = mode.kind === "edit" ? mode.channel : null;

  const typesQ = useQuery({
    queryKey: ["channels", "types"],
    queryFn: channelsApi.types,
    enabled: open,
  });

  // Default: pick the first non-singleton type ("email").
  const [type, setType] = React.useState<ChannelType>(
    editTarget?.type ?? "email",
  );
  const [name, setName] = React.useState(editTarget?.name ?? "");
  const [description, setDescription] = React.useState(editTarget?.description ?? "");
  const [tags, setTags] = React.useState<string[]>(editTarget?.tags ?? []);
  const [enabled, setEnabled] = React.useState(editTarget?.enabled ?? true);
  const [credentialId, setCredentialId] = React.useState<string | null>(
    editTarget?.credential_id ?? null,
  );
  // Per-type config state. We keep one shared object — fields not
  // relevant to the current type are simply ignored at submit time.
  const [config, setConfig] = React.useState<Record<string, unknown>>(
    editTarget?.config ?? {},
  );

  React.useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setType(editTarget.type);
      setName(editTarget.name);
      setDescription(editTarget.description ?? "");
      setTags(editTarget.tags ?? []);
      setEnabled(editTarget.enabled);
      setCredentialId(editTarget.credential_id ?? null);
      setConfig(editTarget.config ?? {});
    } else {
      setType("email");
      setName("");
      setDescription("");
      setTags([]);
      setEnabled(true);
      setCredentialId(null);
      setConfig({});
    }
  }, [open, editTarget]);

  // Non-singleton types only — in_app can't be created.
  const creatableTypes = React.useMemo(
    () => (typesQ.data ?? []).filter((t) => !t.is_singleton),
    [typesQ.data],
  );
  const typeInfo: ChannelTypeInfo | undefined = React.useMemo(
    () => (typesQ.data ?? []).find((t) => t.type_id === type),
    [typesQ.data, type],
  );

  // Reset credential + config when changing type during create.
  const prevType = React.useRef(type);
  React.useEffect(() => {
    if (isCreate && prevType.current !== type) {
      setCredentialId(null);
      setConfig({});
    }
    prevType.current = type;
  }, [type, isCreate]);

  const submit = useMutation<Channel, Error>({
    mutationFn: async () => {
      const payload = {
        type,
        name: name.trim(),
        description: description.trim() || null,
        config,
        credential_id: credentialId,
        enabled,
        tags,
      };
      if (isCreate) return channelsApi.create(payload);
      return channelsApi.update(editTarget!.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      onOpenChange(false);
    },
  });

  const requiresCredential = typeInfo?.credential_type !== null;
  const canSubmit =
    name.trim().length > 0 &&
    (!requiresCredential || credentialId !== null) &&
    !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isCreate ? "New channel" : `Edit ${editTarget?.name}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {/* Type picker (create only) */}
          {isCreate && (
            <div className="space-y-1.5">
              <Label>Type</Label>
              {typesQ.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {creatableTypes.map((t) => (
                    <button
                      key={t.type_id}
                      type="button"
                      onClick={() => setType(t.type_id)}
                      className={cn(
                        "rounded-md border px-2.5 py-1 text-xs",
                        type === t.type_id
                          ? "border-accent bg-accent/10 text-foreground"
                          : "border-border bg-surface text-muted-foreground hover:border-muted-foreground",
                      )}
                      title={t.description}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
              {typeInfo?.description && (
                <p className="text-xs text-muted-foreground">{typeInfo.description}</p>
              )}
            </div>
          )}

          {/* Name + description + tags */}
          <div className="space-y-1.5">
            <Label htmlFor="ch-name">Name</Label>
            <Input
              id="ch-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ops email"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ch-desc">Description (optional)</Label>
            <Input
              id="ch-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this channel deliver?"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ch-tags">Tags</Label>
            <TagsField id="ch-tags" value={tags} onChange={setTags} />
          </div>

          {/* Credential picker (when required by type) */}
          {typeInfo?.credential_type && (
            <div className="space-y-1.5 border-t border-border/60 pt-3">
              <Label>Credential</Label>
              <CredentialPicker
                value={credentialId}
                onChange={setCredentialId}
                type={typeInfo.credential_type as CredentialType}
                emptyText={`No ${typeInfo.credential_type} credentials — create one first in /credentials.`}
              />
              <p className="text-[11px] text-muted-foreground">
                Filtered to <span className="font-mono">{typeInfo.credential_type}</span> credentials.
              </p>
            </div>
          )}

          {/* Type-specific config — handcrafted per type because
              schemas are small and per-field UX (chips for emails,
              etc.) is worth the line count. */}
          {type === "email" && (
            <div className="space-y-3 border-t border-border/60 pt-3">
              <div className="space-y-1.5">
                <Label htmlFor="ch-to">Recipients</Label>
                <TagsField
                  id="ch-to"
                  value={(config.to_addresses as string[]) ?? []}
                  onChange={(v) => setConfig({ ...config, to_addresses: v })}
                  placeholder="ops@acme.com — press Enter to add"
                />
                <p className="text-[11px] text-muted-foreground">
                  At least one address required. Up to 20.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ch-prefix">Subject prefix</Label>
                <Input
                  id="ch-prefix"
                  value={(config.subject_prefix as string) ?? "[Tray]"}
                  onChange={(e) => setConfig({ ...config, subject_prefix: e.target.value })}
                  placeholder="[Tray]"
                />
                <p className="text-[11px] text-muted-foreground">
                  Prepended to every Subject. Default: <span className="font-mono">[Tray]</span>
                </p>
              </div>
            </div>
          )}

          {type === "telegram" && (
            <div className="border-t border-border/60 pt-3 text-xs text-muted-foreground">
              No extra config needed. The chat_id + parse_mode are stored on
              the Telegram credential — one credential = one destination chat.
            </div>
          )}

          {type === "in_app" && (
            <div className="border-t border-border/60 pt-3 text-xs text-muted-foreground">
              in_app is a singleton seeded automatically — no instances to create.
            </div>
          )}

          <label className="flex items-center gap-2 pt-1 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span>Enabled</span>
          </label>

          {submit.error && (
            <Alert tone="danger" title="Save failed">
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => submit.mutate()}>
            {submit.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            )}
            {isCreate ? "Create channel" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
